import { useEffect } from 'react';

/**
 * S1 stopgap for "backing out of a module loses everything I typed":
 * warns the user (native beforeunload dialog) when a dirty form is about to
 * be lost via tab close / refresh / hard navigation. In-app SPA route
 * changes are NOT covered by beforeunload — those are protected by the
 * per-module autosave (localStorage draft + restore), not this hook.
 *
 * Usage: `const dirty = ...; useUnsavedGuard(dirty);`
 */
export function useUnsavedGuard(dirty: boolean, message = 'You have unsaved changes. If you leave now, your work will be lost.') {
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = message; // Chrome/Edge + Safari
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, message]);
}

/**
 * Autosave a serializable value to localStorage under `key` and return the
 * last saved value + a discard helper. Writes on every `value` change.
 */
export function useAutosave(key: string, value: unknown, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    try {
      localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
    } catch {
      // storage full/unavailable — autosave is best-effort
    }
  }, [key, value, enabled]);
}

/** Clear an autosave draft (call after a successful submit). */
export function clearAutosave(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // best-effort
  }
}

/** Read an autosave draft; null when absent/corrupt. */
export function readAutosave<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return (JSON.parse(raw).value ?? null) as T | null;
  } catch {
    return null;
  }
}
